import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useApartments } from '../lib/hooks/useApartments';
import { Link } from 'react-router-dom';

const ApartmentCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize filters from URL parameters
  const [selectedEntrance, setSelectedEntrance] = useState<string>(
    searchParams.get('entrance') || 'всички'
  );
  const [selectedFloor, setSelectedFloor] = useState<string>(
    searchParams.get('floor') || 'всички'
  );
  const [selectedType, setSelectedType] = useState<string>(
    searchParams.get('type') || 'всички'
  );
  const [selectedStatus, setSelectedStatus] = useState<string>(
    searchParams.get('status') || 'всички'
  );

  const { apartments, loading, error } = useApartments();

  // Update URL when filters change
  const updateFilters = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value === 'всички') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    
    setSearchParams(newParams);
  };

  const handleEntranceChange = (value: string) => {
    setSelectedEntrance(value);
    updateFilters('entrance', value);
  };

  const handleFloorChange = (value: string) => {
    setSelectedFloor(value);
    updateFilters('floor', value);
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    updateFilters('type', value);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    updateFilters('status', value);
  };
  // Filter apartments based on all selected criteria
  const filteredApartments = apartments.filter(apartment => {
    // Filter by entrance
    if (selectedEntrance !== 'всички' && apartment.entrance !== selectedEntrance) {
      return false;
    }

    // Filter by floor
    if (selectedFloor !== 'всички' && apartment.floor !== parseInt(selectedFloor)) {
      return false;
    }

    // Filter by type
    if (selectedType !== 'всички' && apartment.type !== selectedType) {
      return false;
    }

    // Filter by status
    if (selectedStatus !== 'всички' && apartment.status !== selectedStatus) {
      return false;
    }

    return true;
  });

  const getExposureText = (exposure: string) => {
    switch (exposure) {
      case 'north': return 'Север';
      case 'south': return 'Юг';
      case 'east': return 'Изток';
      case 'west': return 'Запад';
      case 'northeast': return 'Североизток';
      case 'northwest': return 'Северозапад';
      case 'southeast': return 'Югоизток';
      case 'southwest': return 'Югозапад';
      default: return exposure || '-';
    }
  };

  const getEntranceLabel = (entrance: string) => {
    return entrance === '1' ? 'А' : entrance === '2' ? 'Б' : entrance;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <section className="py-12 bg-white">
      <div className="container-custom">
        <h1 className="text-4xl font-bold text-center mb-12">"Сграда Molino"</h1>
        <h2 className="text-2xl text-center mb-8">Апартаменти</h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="relative">
            <select
              value={selectedEntrance}
              onChange={(e) => handleEntranceChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg appearance-none bg-white pr-8"
            >
              <option value="всички">Всички входове</option>
              <option value="1">Вход А</option>
              <option value="2">Вход Б</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={selectedFloor}
              onChange={(e) => handleFloorChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg appearance-none bg-white pr-8"
            >
              <option value="всички">Всички етажи</option>
              {Array.from({ length: 11 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>Етаж {i + 1}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg appearance-none bg-white pr-8"
            >
              <option value="всички">Всички типове</option>
              <option value="studio">Студио</option>
              <option value="1-bedroom">Едностаен</option>
              <option value="2-bedroom">Двустаен</option>
              <option value="2-bedroom-maisonette">Двустаен мезонет</option>
              <option value="3-bedroom">Тристаен</option>
              <option value="3-bedroom-maisonette">Тристаен мезонет</option>
              <option value="4-bedroom-maisonette">Четиристаен мезонет</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg appearance-none bg-white pr-8"
            >
              <option value="всички">Статус</option>
              <option value="available">Свободен</option>
              <option value="reserved">Резервиран</option>
              <option value="sold">Продаден</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-primary text-white">
                <th className="p-3 text-left">Вход</th>
                <th className="p-3 text-left">Апартамент</th>
                <th className="p-3 text-left">Етаж</th>
                <th className="p-3 text-left">Тип</th>
                <th className="p-3 text-left">Изложение</th>
                <th className="p-3 text-left">Чиста площ</th>
                <th className="p-3 text-left">Обща площ</th>
                <th className="p-3 text-left">Цена</th>
                <th className="p-3 text-left">Статус</th>
              </tr>
            </thead>
            <tbody>
              {filteredApartments.map((apartment, index) => (
                <tr 
                  key={apartment.id}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors cursor-pointer`}
                  onClick={() => window.location.href = `/apartments/${apartment.id}?${searchParams.toString()}`}
                >
                  <td className="p-3 border-b border-gray-200">{getEntranceLabel(apartment.entrance)}</td>
                  <td className="p-3 border-b border-gray-200">
                    <Link 
                      to={`/apartments/${apartment.id}?${searchParams.toString()}`}
                      className="text-primary hover:text-primary-dark transition-colors"
                    >
                      {apartment.number}
                    </Link>
                  </td>
                  <td className="p-3 border-b border-gray-200">{apartment.floor}</td>
                  <td className="p-3 border-b border-gray-200">
                    {apartment.type === 'studio' && 'Студио'}
                    {apartment.type === '1-bedroom' && 'Едностаен'}
                    {apartment.type === '2-bedroom' && 'Двустаен'}
                    {apartment.type === '2-bedroom-maisonette' && 'Двустаен мезонет'}
                    {apartment.type === '3-bedroom' && 'Тристаен'}
                    {apartment.type === '3-bedroom-maisonette' && 'Тристаен мезонет'}
                    {apartment.type === '4-bedroom-maisonette' && 'Четиристаен мезонет'}
                  </td>
                  <td className="p-3 border-b border-gray-200">{getExposureText(apartment.exposure)}</td>
                  <td className="p-3 border-b border-gray-200">
                    {apartment.netArea ? `${apartment.netArea} кв.м` : '-'}
                  </td>
                  <td className="p-3 border-b border-gray-200">{apartment.area} кв.м</td>
                  <td className="p-3 border-b border-gray-200">
                    {apartment.price > 0 ? `€${apartment.price.toLocaleString()}` : ''}
                  </td>
                  <td className="p-3 border-b border-gray-200">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      apartment.status === 'available' 
                        ? 'bg-green-100 text-green-800'
                        : apartment.status === 'reserved'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {apartment.status === 'available' 
                        ? 'Свободен' 
                        : apartment.status === 'reserved'
                        ? 'Резервиран'
                        : 'Продаден'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredApartments.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Няма намерени апартаменти
              </h3>
              <p className="text-gray-500">
                Опитайте с различни критерии за филтриране
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ApartmentCatalog;